/**
 * MY ARTICLES — the list, and the only place the author's lifecycle controls live.
 *
 * The prototype draws this list, its status chips and its sorts well, and draws NO ROW ACTION at
 * all: `>Unarchive`, `>Delete`, `>Pull` and `>Restore` are zero across the whole prototype tree,
 * and the one "Withdraw from review" in it belongs to the APPLICATION rather than to an article.
 * So the row actions here are new work, not a copy.
 *
 * FIVE ACTIONS, EACH WITH A FROM-STATE THE SERVER ENFORCES — see ./article-actions.ts.
 *
 * NO SORT AND NO CATEGORY FILTER, AND THAT IS THE CONTRACT SPEAKING. `listOwnArticles` is
 * newest-first with NO sort parameter, and `ArticleListItem` carries no category, no view count and
 * no comment count. The prototype's four sorts (Last edited / Oldest / Most views / Most comments)
 * and its category filter have nothing to apply, and inventing a query parameter is not what
 * "columns and layout are ours" means. The numbers are Statistics' own, joined in (decision 4 below).
 *
 * STATUS FILTERING IS REAL, because `status` IS a declared query parameter and it takes several
 * values. What it cannot do is show a count on each chip: there is no aggregate-counts operation,
 * so a count per chip would need eight calls. `total` on the response gives the count for the
 * filter actually applied - shown under the chips (R-035), and with several chips it is the
 * combined count, so it says "match", never a number per chip.
 *
 * KEYSET PAGING, NEVER NUMBERED. See ../../features/cabinet/state/use-keyset-list.ts.
 *
 * ★ DESIGN 15 - THE LIST AS THE PROTOTYPE'S CARDS (the owner's live look at real cabinets,
 * 2026-10-01: "adapt UI/styles/layouts ... to this designer variant"; the prototype's "Content",
 * `cabIsMine`). Each article is the kit's `ItemCard`: its cover, or "No cover"; its status - and
 * "Needs a cover" / "N to resolve" - in small capitals over the title; the title, still the in-app
 * link to its editor; the standfirst; a gold note where the status has something to say ("Goes
 * live …", "The review asked for changes"); a line of facts - when it was published, or last edited
 * (`whenLine`, at the foot) - with its all-time views and its likes and comments (decision 4,
 * ./card-figures.tsx: ONE `listArticleStats` read per page load, joined by `articleId`); and its
 * actions at the right (./ArticleRowActions.tsx). The status chips are the prototype's pills,
 * still several at once (./StatusFilter.tsx). PLAIN WORDS: nothing on a card says where an article
 * came from - an article brought into the cabinet is simply the author's, so the "Brought over from
 * the old app" badge and "published in the old app" are gone; "Your #N" went with them, and "Your
 * first article" stays only where it explains a legitimate zero (`firstArticleBadge`).
 *
 * ★ VIDEOS (v1.0.29). "+ New" is a menu (./NewMenu.tsx): write an article, or - by the author's
 * video access - upload a video, ask for access, or see that the ask is with staff. Once access is
 * granted or asked for, a switch shows Articles or Videos: the same list, read with `kind: 'video'`,
 * each video opening the video editor. A video carries its own figures (`listVideoStats`), not Statistics'.
 * After staff withdraw access the switch stays while the author has any video (`useHasVideos`): a
 * published one is still theirs to archive, and one sent back still counts in "Needs attention",
 * which links here with `?show=videos`.
 *
 * ★ LIVE STREAMS (1.2.0). "+ New" has a "Live stream" row by the author's stream access, a switch
 * of its own; stream access offers the Videos list as video access does, and a stream's card there
 * says it is one and when it starts (`streamStartsAt`, on the author's clock).
 */
import { Alert, Badge, Button, Stack, Text } from '@mantine/core';
import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { CalendarDays, CircleAlert, Clock, Hourglass, Info } from '@moonx/ui';
import { describeProblem, hasErrorCode, useCabinetApi } from '../../features/cabinet/api';
import { STREAM_ERROR_CODE, VIDEO_ERROR_CODE } from '../../features/cabinet/api/editor-api';
import {
  CabinetPageHeader,
  EmptyState,
  ErrorState,
  ItemCard,
  LoadMore,
  LoadingState,
  MetaPart,
  PillChoice,
  RefreshingNote,
  StatusPill,
} from '../../features/cabinet/components';
import { useAction, useAsync, useKeysetList } from '../../features/cabinet/state';
import { formatDayRange, formatInstant } from '../../features/cabinet/format';
import { InAppAnchor } from '../../features/shell/InAppAnchor';
import { useNavigateToHref } from '../../features/shell/in-app-navigation';
import type { NavigateToHref } from '../../features/shell/in-app-navigation';
import { emulatorImageUrl, servedUrlOnError } from '../../features/storage/emulator-image-url';
import { Trans, useTranslation } from '../../i18n';
import { VIDEOS_TAB_QUERY, videoEditorHrefFor } from '../videos/paths';
import { useVideoAccess } from '../videos/use-video-access';
import { ArticleRowActions } from './ArticleRowActions';
import { editorHrefFor } from './EditorRoute';
import { NewMenu } from './NewMenu';
import { StatusFilter } from './StatusFilter';
import { CardFigures, VideoCardFigures, useCardFigures, useVideoFigures } from './card-figures';
import type { VideoAccess } from '../../features/cabinet/api/generated';
import type { ArticleActionKind } from './article-actions';
import type { ArticlesT } from './articles-i18n';
import type {
  ArticleListItem,
  ArticleListPage,
  ArticleStats,
  VideoStats,
  ArticleStatus,
  PublicId,
} from '../../features/cabinet/api';
import classes from './articles.module.css';

/**
 * THE "N TO RESOLVE" BADGE, DRAWN AS THE STATUS PILL BESIDE IT IS. Its warning tint lies on an
 * opaque base of its own, the card colour, so nothing under it reaches what its words are read
 * against - the old badge, in a table row that highlighted on hover, read 1.68 over light's old grey
 * hover fill, 3.47 over the `bg2` that replaced it; the base is why it reads the same on the
 * design-15 card (`--mx-surface`). The words take the warning INK, web/ui's per-scheme alias (light
 * `accentHover`, dark `warning`): light `warning` on its own tint read 3.78 at rest. Derived: light
 * 6.21, dark 8.54 (web/authors/test/tinted-inks.spec.tsx).
 */
const WARNING_TINT = 'oklch(var(--mx-warning) / 0.14)';
const TO_RESOLVE_BADGE = {
  backgroundColor: 'var(--mantine-color-body)',
  backgroundImage: `linear-gradient(${WARNING_TINT}, ${WARNING_TINT})`,
  color: 'oklch(var(--mx-warning-ink))',
} as const;

/**
 * "YOUR FIRST ARTICLE" - THE ONE ORDINAL A CARD STILL NAMES, because it explains a legitimate zero:
 * ordinal 1 earns nothing, ever (D7). Not for an article brought into the cabinet (design 14, F2:
 * the move numbered the earlier pieces, so its ordinal 1 is an old piece, and the walk of 2026-10-01
 * found one badged "Your first article"); design 15 says nothing special about that one at all.
 * The pay rule is unchanged - only the words.
 */
function firstArticleBadge(article: ArticleListItem, t: ArticlesT): string | undefined {
  return article.publicationOrdinal === 1 && article.importedFromOldApp !== true
    ? t('list.card.firstArticle')
    : undefined;
}

/** The server's `total` for the filter applied - every page of it, not the rows on screen. */
function matchWords(total: number, filtered: boolean, t: ArticlesT): string {
  return filtered ? t('list.matchFilter', { count: total }) : t('list.inAll', { count: total });
}

export interface ArticlesRouteProps {
  /**
   * Where the editor lives.
   *
   * ★ IT NOW HAS A DEFAULT, AND THE PARAGRAPH THAT SAID IT COULD NOT IS STRUCK. This prop used to
   * read *"This track does not own that route, so the link is handed in"* and nothing ever handed
   * one in — so every title in this table was inert text and `createDraft` left the author holding
   * a row they could not open. `./EditorRoute.tsx` is that route; `editorHrefFor` is its address,
   * stated there once. The prop stays so a shell can still override it.
   */
  editorHref?: (articleId: PublicId) => string;
  /** Where a video opens (v1.0.29): the video editor. */
  videoEditorHref?: (articleId: PublicId) => string;
  /** Where the per-article numbers live. Defaults to the sibling route below. */
  statsHref?: string;
  /**
   * How the author is moved on after a new draft is created. Omitted, it is the app's in-app
   * navigation (`features/shell/in-app-navigation.ts`): under the app's router the move is made in
   * place, and a screen mounted with no router - a spec, a preview - still gets a full page load.
   * ★ UNTIL 2026-09-23 THE DEFAULT WAS THAT FULL PAGE LOAD, and the router renders this screen with
   * no props, so every new draft reloaded the whole app on its way into the editor.
   */
  navigate?: NavigateToHref;
}

/** Which list is shown: the articles, or (v1.0.29) the videos. */
type Shown = 'articles' | 'videos';

/**
 * Whether the author has any video, asked only when `ask` - access neither granted nor asked for,
 * where nothing else on the page says so: one row of the videos, in any state.
 */
function useHasVideos(ask: boolean): boolean {
  const api = useCabinetApi();
  const read = useCallback(
    (signal: AbortSignal) =>
      ask ? api.listOwnArticles({ kind: 'video', limit: 1 }, signal) : Promise.resolve(undefined),
    [api, ask],
  );
  const page = useAsync(read, `articles:has-videos:${String(ask)}`);
  return ask && (page.data?.items.length ?? 0) > 0;
}

/** The video editor's crumb comes back with `?show=videos`, so the list opens on its videos. */
function openingList(): Shown {
  const [key, value] = VIDEOS_TAB_QUERY.split('=');
  return new URLSearchParams(window.location.search).get(key ?? '') === value
    ? 'videos'
    : 'articles';
}

export function ArticlesRoute({
  editorHref = editorHrefFor,
  videoEditorHref = videoEditorHrefFor,
  statsHref = '/articles/statistics',
  navigate: navigateOverride,
}: ArticlesRouteProps) {
  const { t } = useTranslation('articles');
  const { t: tv } = useTranslation('video');
  const inApp = useNavigateToHref();
  const navigate = navigateOverride ?? inApp;
  const api = useCabinetApi();
  const [selected, setSelected] = useState<readonly ArticleStatus[]>([]);
  const video = useVideoAccess();
  const stream = useVideoAccess('stream');
  // The switch is there once video or live streams are the author's or asked for, or while the
  // author has a video; before that, only articles.
  const offers = (access: VideoAccess | undefined) =>
    access === 'granted' || access === 'requested';
  const accessOffered = offers(video.access) || offers(stream.access);
  const hasVideos = useHasVideos(
    video.access !== undefined && stream.access !== undefined && !accessOffered,
  );
  const videosOffered = accessOffered || hasVideos;
  const [chosen, setChosen] = useState<Shown>(openingList);
  const shown: Shown = videosOffered ? chosen : 'articles';
  const videos = shown === 'videos';
  const [busy, setBusy] = useState<{ articleId: string; kind: ArticleActionKind } | undefined>(
    undefined,
  );

  // The key is the filter, spelled out. Changing it throws the accumulated pages away, which is
  // right: a filter change is a different question, not more of the same answer.
  const key = useMemo(() => `${shown}:${[...selected].sort().join(',')}`, [shown, selected]);

  const read = useCallback(
    (cursor: string | undefined, signal: AbortSignal) =>
      api.listOwnArticles(
        {
          ...(videos ? { kind: 'video' as const } : {}),
          ...(selected.length === 0 ? {} : { status: selected }),
          ...(cursor === undefined ? {} : { cursor }),
        },
        signal,
      ),
    [api, selected, videos],
  );

  const list = useKeysetList<ArticleListItem, ArticleListPage>(read, key);
  const total = list.lastPage?.total;
  // Decision 4: the cards' all-time views, likes and comments - one read, whatever the filter does.
  const figures = useCardFigures();
  const videoFigures = useVideoFigures(videos);

  const create = useAction(() => api.createDraft());
  const createVideo = useAction(async () => {
    try {
      return await api.createVideoDraft();
    } catch (error) {
      // Staff took video back after this page read the session: read it again, so the menu
      // offers what is true now. The refusal is still said.
      if (hasErrorCode(error, VIDEO_ERROR_CODE.accessRequired)) {
        video.recheck();
      }
      throw error;
    }
  });
  const createStream = useAction(async () => {
    try {
      return await api.createVideoDraft({ stream: true });
    } catch (error) {
      // As for video: staff took live streams back after the session was read.
      if (hasErrorCode(error, STREAM_ERROR_CODE.accessRequired)) {
        stream.recheck();
      }
      throw error;
    }
  });
  /* Which list an action ran from: its refusal is worded for that kind of piece, even once the
     switch has moved on. */
  const [actedOn, setActedOn] = useState<Shown>('articles');

  const runAction = useAction(async (kind: ArticleActionKind, article: ArticleListItem) => {
    switch (kind) {
      case 'withdraw':
        return await api.withdrawArticle(article.articleId);
      case 'pull':
        return await api.pullScheduledArticle(article.articleId);
      case 'archive':
        return await api.archiveOwnArticle(article.articleId);
      case 'unarchive':
        return await api.unarchiveOwnArticle(article.articleId);
      case 'delete':
        return await api.deleteNeverPublishedDraft(article.articleId);
      default:
        // `ArticleActionKind` is closed; this is here so adding a kind is a compile error.
        throw new Error(`Unhandled article action: ${String(kind)}`);
    }
  });

  const onRun = useCallback(
    async (kind: ArticleActionKind, article: ArticleListItem) => {
      setBusy({ articleId: article.articleId, kind });
      setActedOn(shown);
      await runAction.run(kind, article);
      setBusy(undefined);
      // Re-read rather than patch. A `TransitionResult` is the move and not the row, and another
      // tab may have moved something else in the meantime.
      list.reload();
    },
    [runAction, list, shown],
  );

  const toggleStatus = useCallback((status: ArticleStatus) => {
    setSelected((current) =>
      current.includes(status) ? current.filter((value) => value !== status) : [...current, status],
    );
  }, []);

  const clearFilter = useCallback(() => {
    setSelected([]);
  }, []);

  return (
    <Stack gap="lg">
      <CabinetPageHeader
        title={t('list.title')}
        lede={t('list.lede')}
        actions={
          <NewMenu
            videoAccess={video.access}
            streamAccess={stream.access}
            authorStatus={video.authorStatus}
            busy={
              create.isRunning ||
              createVideo.isRunning ||
              createStream.isRunning ||
              video.requesting ||
              stream.requesting
            }
            onWriteArticle={() => {
              void (async () => {
                const draft = await create.run();
                if (draft !== undefined) {
                  /* ★ AND NOW IT GOES SOMEWHERE. `createDraft` answers 201 with the new, empty
                     WORKING COPY — not a list row — so the id for the editor is on the response
                     and no re-read is needed to find it. Before the editor route existed this
                     branch only reloaded the table, which left the author looking at "Untitled"
                     with nothing to click. */
                  navigate(editorHref(draft.articleId));
                }
              })();
            }}
            onUploadVideo={() => {
              void (async () => {
                const draft = await createVideo.run();
                if (draft !== undefined) {
                  navigate(videoEditorHref(draft.articleId));
                }
              })();
            }}
            onRequestAccess={() => {
              void video.request();
            }}
            onStartStream={() => {
              void (async () => {
                const draft = await createStream.run();
                if (draft !== undefined) {
                  navigate(videoEditorHref(draft.articleId));
                }
              })();
            }}
            onRequestStreamAccess={() => {
              void stream.request();
            }}
          />
        }
      />

      {create.error === undefined ? null : (
        <ErrorState title={t('list.draftNotCreated')} error={create.error} />
      )}
      {createVideo.error === undefined ? null : (
        <ErrorState title={tv('new.videoNotCreated')} error={createVideo.error} />
      )}
      {video.error === undefined ? null : (
        <ErrorState title={tv('new.requestFailed')} error={video.error} />
      )}
      {video.justRequested ? (
        <Alert variant="light" role="status" title={tv('new.requestSentTitle')}>
          <Text size="sm">{tv('new.requestSent')}</Text>
        </Alert>
      ) : null}
      {createStream.error === undefined ? null : (
        <ErrorState title={tv('new.streamNotCreated')} error={createStream.error} />
      )}
      {stream.error === undefined ? null : (
        <ErrorState title={tv('new.requestFailed')} error={stream.error} />
      )}
      {stream.justRequested ? (
        <Alert variant="light" role="status" title={tv('new.requestSentTitle')}>
          <Text size="sm">{tv('new.streamRequestSent')}</Text>
        </Alert>
      ) : null}
      {runAction.error === undefined ? null : (
        <Alert
          variant="light"
          role="alert"
          title={t('common.didNotGoThrough')}
          withCloseButton
          closeButtonLabel={t('common.dismiss')}
          onClose={runAction.clearError}
          styles={{
            root: {
              backgroundColor: 'oklch(var(--mx-danger) / 0.10)',
              border: '1px solid oklch(var(--mx-danger) / 0.45)',
            },
            title: { color: 'oklch(var(--mx-danger))' },
          }}
        >
          <Text size="sm">
            {describeProblem(runAction.error, actedOn === 'videos' ? 'video' : '')}
          </Text>
        </Alert>
      )}

      <Stack gap={10}>
        {videosOffered ? (
          <PillChoice<Shown>
            label={tv('list.switchLabel')}
            options={[
              { value: 'articles', label: tv('list.articles') },
              { value: 'videos', label: tv('list.videos') },
            ]}
            value={shown}
            onChange={setChosen}
          />
        ) : null}
        <StatusFilter selected={selected} onToggle={toggleStatus} onClear={clearFilter} />
        {/* A status, so a filter change is announced; absent while the first page is loading. The
            count's words name articles, so the videos' list goes without it. */}
        {total === undefined || videos ? null : (
          <Text size="sm" c="dimmed" role="status">
            {matchWords(total, selected.length > 0, t)}
          </Text>
        )}
      </Stack>

      {list.isInitialLoad ? (
        <LoadingState label={videos ? tv('list.reading') : t('list.reading')} rows={5} />
      ) : list.status === 'error' && list.items.length === 0 ? (
        <ErrorState
          title={videos ? tv('list.didNotLoad') : t('list.didNotLoad')}
          error={list.error}
          onRetry={list.reload}
        />
      ) : list.items.length === 0 ? (
        <EmptyState
          headline={
            selected.length > 0
              ? t('list.empty.filteredTitle')
              : videos
                ? tv('list.emptyTitle')
                : t('list.empty.noneTitle')
          }
          explanation={
            selected.length > 0
              ? videos
                ? tv('list.filteredText')
                : t('list.empty.filteredText')
              : videos
                ? video.access === 'granted'
                  ? tv('list.emptyText')
                  : stream.access === 'granted'
                    ? tv('list.emptyTextStream')
                    : tv('list.emptyTextWaiting')
                : t('list.empty.noneText')
          }
          action={
            selected.length === 0 ? null : (
              <Button variant="default" onClick={clearFilter}>
                {t('list.clearFilter')}
              </Button>
            )
          }
        />
      ) : (
        <Stack gap="sm">
          {list.status === 'error' ? (
            // Partial: cards are on screen and the last read failed. Keep the cards.
            <ErrorState title={t('list.lastReadFailed')} error={list.error} onRetry={list.reload} />
          ) : null}
          {list.isLoadingMore ? <RefreshingNote label={t('list.loadingMore')} /> : null}

          <Stack
            component="ul"
            gap={12}
            aria-label={videos ? tv('list.listLabel') : t('list.listLabel')}
            style={{ listStyle: 'none', margin: 0, padding: 0 }}
          >
            {list.items.map((article) => (
              <li key={article.articleId}>
                <ArticleCard
                  article={article}
                  editorHref={
                    videos ? videoEditorHref(article.articleId) : editorHref(article.articleId)
                  }
                  untitled={videos ? tv('list.untitled') : t('common.untitled')}
                  figures={videos ? undefined : figures.byArticle?.get(article.articleId)}
                  videoFigures={videos ? videoFigures?.get(article.articleId) : undefined}
                  actions={
                    <ArticleRowActions
                      article={article}
                      busyKind={busy?.articleId === article.articleId ? busy.kind : undefined}
                      onRun={onRun}
                    />
                  }
                />
              </li>
            ))}
          </Stack>

          <LoadMore
            hasMore={list.hasMore}
            isLoading={list.isLoadingMore}
            noun={videos ? tv('list.loadMoreNoun') : t('list.loadMoreNoun')}
            count={list.items.length}
            onLoadMore={list.loadMore}
          />

          {videos ? null : (
            <Text size="xs" c="dimmed">
              {/* Which days the cards' figures cover, once the read has answered: the views all of
                them (the owner, 2026-10-05; ./card-figures.tsx), likes and comments the read's. */}
              {figures.byArticle === undefined
                ? null
                : figures.from === undefined || figures.to === undefined
                  ? t('list.figuresAllTime')
                  : t('list.figuresWithRange', {
                      range: formatDayRange(figures.from, figures.to),
                    })}
              {/* The link's words are the catalogue's, put in its place by `Trans`. */}
              <Trans
                t={t}
                i18nKey="list.fullFigures"
                components={{
                  anchor: (
                    <InAppAnchor href={statsHref} size="xs">
                      {null}
                    </InAppAnchor>
                  ),
                }}
              />
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  );
}

/** The statuses a piece reaches only by having been published: each is dated by its publication. */
const DATED_BY_PUBLICATION: ReadonlySet<ArticleStatus> = new Set([
  'published',
  'unpublished',
  'archived',
]);

/**
 * ★ WHICH DATE A CARD GIVES (the owner's live walk of real cabinets, 2026-10-01, and design 15).
 * `updatedAt` is the row's last write, and for every article brought into the cabinet that is the
 * move itself: an author with 130 pieces written over years saw the same day - the day of the move
 * - on all of them. A piece that has been published (live, taken down or archived) is dated by its
 * publication, `publishedAt`, which keeps the day it was first published wherever that was; every
 * other piece by its last edit. Design 14 said where such a date came from ("published in the old
 * app"); design 15's plain-words rule drops that - an author's article is simply theirs. A draft
 * brought over has no other date on the list item, so it keeps `updatedAt`; a server member for it
 * is owed (reported to the owner).
 */
function whenLine(article: ArticleListItem, t: ArticlesT): string {
  if (DATED_BY_PUBLICATION.has(article.status) && typeof article.publishedAt === 'string') {
    return t('list.card.published', { when: formatInstant(article.publishedAt) });
  }
  return t('list.card.edited', { when: formatInstant(article.updatedAt) });
}

/** The gold line under the standfirst, where the status has something to say (prototype `al2.note`). */
function noteFor(article: ArticleListItem, t: ArticlesT): ReactNode {
  switch (article.status) {
    case 'scheduled':
      return article.goLiveAt === undefined || article.goLiveAt === null ? undefined : (
        <>
          <Clock size={13} aria-hidden />
          {t('list.card.goesLive', { when: formatInstant(article.goLiveAt) })}
        </>
      );
    case 'changes_requested':
      return (
        <>
          <CircleAlert size={13} aria-hidden />
          {t('list.card.changesRequested')}
        </>
      );
    case 'in_review':
      return (
        <>
          <Hourglass size={13} aria-hidden />
          {t('list.card.inReview')}
        </>
      );
    case 'unpublished':
      return (
        <>
          <Info size={13} aria-hidden />
          {t('list.card.unpublished')}
        </>
      );
    default:
      return undefined;
  }
}

interface ArticleCardProps {
  readonly article: ArticleListItem;
  readonly editorHref: string;
  /** What an empty title reads: "Untitled", or for a video "Untitled video". */
  readonly untitled: string;
  /** This article's row from the one statistics read, when the read answered and holds it. */
  readonly figures: ArticleStats | undefined;
  /** A video's own figures (v1.0.29), from `listVideoStats`. */
  readonly videoFigures?: VideoStats | undefined;
  readonly actions: ReactNode;
}

function ArticleCard({
  article,
  editorHref,
  untitled,
  figures,
  videoFigures,
  actions,
}: ArticleCardProps) {
  const { t } = useTranslation('articles');
  const { t: tv } = useTranslation('video');
  const title = article.title.length === 0 ? untitled : article.title;
  const first = firstArticleBadge(article, t);
  // Present only on a live stream; null until its author has set the start.
  const streamStartsAt = article.streamStartsAt;
  const findings = article.openBlockingFindings ?? 0;
  const cover = article.coverUrl ?? null;
  const subtitle = article.subtitle ?? null;

  return (
    <ItemCard
      label={title}
      media={
        cover === null ? undefined : (
          // Through `emulatorImageUrl`, as every picture in this app is: the API's address is
          // production Storage's, which the local stack does not serve.
          <img
            className={classes.cover}
            src={emulatorImageUrl(cover)}
            onError={servedUrlOnError(cover)}
            alt=""
            loading="lazy"
          />
        )
      }
      eyebrow={
        <>
          <StatusPill status={article.status} />
          {first === undefined ? null : (
            <Badge variant="default" size="xs" radius="sm">
              {first}
            </Badge>
          )}
          {streamStartsAt === undefined ? null : (
            <Badge variant="default" size="xs" radius="sm">
              {tv('list.streamBadge')}
            </Badge>
          )}
          {article.awaitingCover === true ? (
            <Badge variant="default" size="xs" radius="sm">
              {t('list.card.needsCover')}
            </Badge>
          ) : null}
          {findings === 0 ? null : (
            <Badge size="xs" radius="sm" styles={{ root: TO_RESOLVE_BADGE }}>
              {t('list.card.toResolve', { count: findings })}
            </Badge>
          )}
        </>
      }
      title={
        /* ALWAYS A LINK, FOLLOWED IN PLACE (`InAppAnchor`): a real `<a href>` to the editor. It
           takes the card title's own size, weight and ink, as the prototype's does. */
        <InAppAnchor href={editorHref} inherit c="inherit" underline="hover">
          {title}
        </InAppAnchor>
      }
      subtitle={subtitle === null || subtitle.length === 0 ? undefined : subtitle}
      note={noteFor(article, t)}
      meta={
        <>
          {streamStartsAt === undefined ? null : (
            <MetaPart icon={<CalendarDays size={13} aria-hidden />}>
              {streamStartsAt === null
                ? tv('list.streamNoStart')
                : tv('list.streamStarts', { when: formatInstant(streamStartsAt) })}
            </MetaPart>
          )}
          <MetaPart icon={<Clock size={13} aria-hidden />}>{whenLine(article, t)}</MetaPart>
          {/* Figures only on a piece that has been published: a draft has nothing to count. */}
          {figures === undefined || typeof article.publishedAt !== 'string' ? null : (
            <CardFigures row={figures} />
          )}
          {videoFigures === undefined || typeof article.publishedAt !== 'string' ? null : (
            <VideoCardFigures row={videoFigures} />
          )}
        </>
      }
      actions={actions}
    />
  );
}
